import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import {
  COLUMN_NAME,
  DATAFILE_ITEM_TYPES,
  FILE_OUTPUT_TYPES,
  INTERPOLATION_METHODS,
  SIGNAL_VALUE_COLUMN_OPTIONS,
  VALIDATION_MODES,
} from '@/tools/importDatafile/importDatafile.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { ItemTypeEnum, ValidationModeEnum } from '@seeqdev/api/model/DatafileInputV1';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { DELIMITERS } from '@/main/app.constants';
import type { BaseToolPersistedState } from '@/tools/tool.types';

export type ImportDatafileOption<TValue = string> = { text: string; value: TValue };
export type ImportDatafileDuration = { value: number | string; units: string | undefined };
export type ImportDatafileTimezone = { displayName: string };
export type ImportDatafileInterpolationMethod = ImportDatafileOption<string>;
export type ImportDatafileValidationMode = ImportDatafileOption<ValidationModeEnum>;
export type ImportDatafileItemType = ImportDatafileOption<ItemTypeEnum>;
export type ImportDatafileValueColumnOption = ImportDatafileOption<string>;
export type ImportDatafileFileOutputType = ImportDatafileOption<string>;
export type ImportDatafileDelimiterOption = (typeof DELIMITERS)[number];
export type ImportDatafileDayFirstOption = ImportDatafileOption<boolean>;

export type ImportDatafileState = BaseToolPersistedState & {
  /** Temporary server upload name, valid only until the server removes the upload. */
  uploadFilename: string | undefined;
  /** Original local filename shown in the form. */
  filename: string | undefined;
  /** CSV column containing timestamps or capsule starts. */
  keyColumnIndex: number;
  /** Optional name for the key column. */
  keyColumnName: string;
  /** Signal interpolation method. */
  interpolationMethod: ImportDatafileInterpolationMethod | undefined;
  /** Handling of invalid CSV values. */
  validationMode: ImportDatafileValidationMode | undefined;
  /** Whether an edited datafile appends or replaces rows. */
  fileOutputType: ImportDatafileFileOutputType | undefined;
  /** Separator used to parse CSV fields. */
  fieldDelimiter: ImportDatafileDelimiterOption | undefined;
  /** Maximum gap interpolated between signal samples. */
  maxInterpolation: ImportDatafileDuration;
  /** Prefix added to imported item names. */
  namePrefix: string;
  /** Suffix added to imported item names. */
  nameSuffix: string;
  /** First CSV row containing data. */
  firstDataRow: number;
  /** Row providing per-signal interpolation gaps. */
  maxInterpolationRow: number | undefined;
  /** Row providing per-signal interpolation methods. */
  interpolationMethodRow: number | undefined;
  /** Row providing units of measure. */
  valueUomRow: number | undefined;
  /** Time zone for timestamps without an explicit zone. */
  timezone: ImportDatafileTimezone | undefined;
  /** Row providing item descriptions. */
  descriptionRow: number | undefined;
  /** A numeric zero marks Condition's "None" column option in the API payload. */
  valueColumnIndices: string | number | undefined;
  /** Edit hydration may supply names as an array; the form normally uses text. */
  valueColumnNames: string | string[] | undefined;
  /** Row providing imported item names. */
  nameRow: number;
  /** Description applied to the datafile. */
  description: string;
  /** Selects all, named, indexed, or no value columns. */
  valueColumnOption: ImportDatafileValueColumnOption;
  /** Unit applied to imported values. */
  valueUom: string;
  /** Whether nonexistent daylight-saving times are accepted. */
  lenientDaylightSavings: boolean;
  /** Whether the CSV creates signals or a condition. */
  itemType: ImportDatafileItemType;
  /** Maximum imported capsule duration. */
  maxDuration: ImportDatafileDuration;
  /** CSV column containing capsule ends. */
  endColumnIndex: number | undefined;
  /** Optional name for the capsule end column. */
  endColumnName: string;
  /** Name of the imported condition. */
  conditionName: string;
  /** Default day and month order for ambiguous dates. */
  dayFirstDefault: ImportDatafileDayFirstOption | undefined;
};

/**
 * Form settings and panel state saved in a worksheet workstep. The picked fields exclude
 * temporary upload state and unused base-tool metadata; optional legacy fields describe older snapshots.
 */
export type ImportDatafileDehydratedState = Pick<
  ImportDatafileState,
  | 'id'
  | 'name'
  | 'advancedParametersCollapsed'
  | 'filename'
  | 'keyColumnIndex'
  | 'keyColumnName'
  | 'interpolationMethod'
  | 'validationMode'
  | 'fileOutputType'
  | 'fieldDelimiter'
  | 'maxInterpolation'
  | 'namePrefix'
  | 'nameSuffix'
  | 'firstDataRow'
  | 'maxInterpolationRow'
  | 'interpolationMethodRow'
  | 'valueUomRow'
  | 'timezone'
  | 'descriptionRow'
  | 'valueColumnIndices'
  | 'valueColumnNames'
  | 'nameRow'
  | 'description'
  | 'valueColumnOption'
  | 'valueUom'
  | 'lenientDaylightSavings'
  | 'itemType'
  | 'maxDuration'
  | 'endColumnIndex'
  | 'endColumnName'
  | 'conditionName'
  | 'dayFirstDefault'
> &
  Partial<Pick<ImportDatafileState, 'configVersion' | 'maximumDuration' | 'originalParameters' | 'uploadFilename'>>;

export type ImportDatafileActions = {
  setUploadFilename: (value: string | undefined) => void;
  setFilename: (value: string | undefined) => void;
  setKeyColumnIndex: (value: number) => void;
  setKeyColumnName: (value: string) => void;
  setInterpolationMethod: (value: ImportDatafileInterpolationMethod | undefined) => void;
  setValidationMode: (value: ImportDatafileValidationMode | undefined) => void;
  setFileOutputType: (value: ImportDatafileFileOutputType | undefined) => void;
  setFieldDelimiter: (value: ImportDatafileDelimiterOption | undefined) => void;
  setMaxInterpolation: (value: ImportDatafileDuration) => void;
  setNamePrefix: (value: string) => void;
  setNameSuffix: (value: string) => void;
  setFirstDataRow: (value: number) => void;
  setMaxInterpolationRow: (value: number | undefined) => void;
  setInterpolationMethodRow: (value: number | undefined) => void;
  setValueUomRow: (value: number | undefined) => void;
  setTimezone: (value: ImportDatafileTimezone | undefined) => void;
  setDescriptionRow: (value: number | undefined) => void;
  setValueColumnIndices: (value: string | number | undefined) => void;
  setValueColumnNames: (value: string | string[] | undefined) => void;
  setNameRow: (value: number) => void;
  setDescription: (value: string) => void;
  setValueColumnOption: (value: ImportDatafileValueColumnOption) => void;
  setValueUom: (value: string) => void;
  setLenientDaylightSavings: (value: boolean) => void;
  setItemType: (value: ImportDatafileItemType) => void;
  setMaxDuration: (value: ImportDatafileDuration) => void;
  setEndColumnIndex: (value: number | undefined) => void;
  setEndColumnName: (value: string) => void;
  setConditionName: (value: string) => void;
  setDayFirstDefault: (value: ImportDatafileDayFirstOption | undefined) => void;
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  rehydrateForEdit: (payload: Partial<ImportDatafileState>) => void;
  reset: () => void;
};

const initialState: ImportDatafileState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  uploadFilename: '',
  filename: '',
  keyColumnIndex: 1,
  keyColumnName: '',
  interpolationMethod: INTERPOLATION_METHODS.find((item) => item.value === 'Linear'),
  validationMode: VALIDATION_MODES.find((item) => item.value === ValidationModeEnum.Fail),
  fileOutputType: FILE_OUTPUT_TYPES.find((item) => item.value === 'Replace'),
  fieldDelimiter: undefined,
  maxInterpolation: { value: 40, units: 'h' },
  namePrefix: '',
  nameSuffix: '',
  firstDataRow: 2,
  maxInterpolationRow: undefined,
  interpolationMethodRow: undefined,
  valueUomRow: undefined,
  timezone: undefined,
  descriptionRow: undefined,
  valueColumnIndices: undefined,
  valueColumnNames: undefined,
  nameRow: 1,
  description: '',
  valueColumnOption: SIGNAL_VALUE_COLUMN_OPTIONS[0],
  valueUom: '',
  lenientDaylightSavings: false,
  itemType: DATAFILE_ITEM_TYPES[0],
  maxDuration: { value: 40, units: 'h' },
  endColumnIndex: undefined,
  endColumnName: '',
  conditionName: '',
  dayFirstDefault: undefined,
});

export const dehydrateImportDatafileState = (state: ImportDatafileState): ImportDatafileDehydratedState => ({
  id: state.id,
  name: state.name,
  advancedParametersCollapsed: state.advancedParametersCollapsed,
  filename: state.filename,
  keyColumnIndex: state.keyColumnIndex,
  keyColumnName: state.keyColumnName,
  interpolationMethod: state.interpolationMethod,
  validationMode: state.validationMode,
  fileOutputType: state.fileOutputType,
  fieldDelimiter: state.fieldDelimiter,
  maxInterpolation: state.maxInterpolation,
  namePrefix: state.namePrefix,
  nameSuffix: state.nameSuffix,
  firstDataRow: state.firstDataRow,
  maxInterpolationRow: state.maxInterpolationRow,
  interpolationMethodRow: state.interpolationMethodRow,
  valueUomRow: state.valueUomRow,
  timezone: state.timezone,
  descriptionRow: state.descriptionRow,
  valueColumnIndices: state.valueColumnIndices,
  valueColumnNames: state.valueColumnNames,
  nameRow: state.nameRow,
  description: state.description,
  valueColumnOption: state.valueColumnOption,
  valueUom: state.valueUom,
  lenientDaylightSavings: state.lenientDaylightSavings,
  itemType: state.itemType,
  maxDuration: state.maxDuration,
  endColumnIndex: state.endColumnIndex,
  endColumnName: state.endColumnName,
  conditionName: state.conditionName,
  dayFirstDefault: state.dayFirstDefault,
});

const importDatafileActionsFactory: ActionsFactory<ImportDatafileState, ImportDatafileActions> = (
  set,
  _get,
  resetState,
) => ({
  setUploadFilename: (value) =>
    set((draft) => {
      draft.uploadFilename = value;
    }),
  setFilename: (value) =>
    set((draft) => {
      draft.filename = value;
    }),
  setKeyColumnIndex: (value) =>
    set((draft) => {
      draft.keyColumnIndex = value;
    }),
  setKeyColumnName: (value) =>
    set((draft) => {
      draft.keyColumnName = value;
    }),
  setInterpolationMethod: (value) =>
    set((draft) => {
      draft.interpolationMethod = value;
    }),
  setValidationMode: (value) =>
    set((draft) => {
      draft.validationMode = value;
    }),
  setFileOutputType: (value) =>
    set((draft) => {
      draft.fileOutputType = value;
    }),
  setFieldDelimiter: (value) =>
    set((draft) => {
      draft.fieldDelimiter = value;
    }),
  setMaxInterpolation: ({ value, units }) =>
    set((draft) => {
      draft.maxInterpolation = { value, units };
    }),
  setNamePrefix: (value) =>
    set((draft) => {
      draft.namePrefix = value;
    }),
  setNameSuffix: (value) =>
    set((draft) => {
      draft.nameSuffix = value;
    }),
  setFirstDataRow: (value) =>
    set((draft) => {
      draft.firstDataRow = value;
    }),
  setMaxInterpolationRow: (value) =>
    set((draft) => {
      draft.maxInterpolationRow = value;
    }),
  setInterpolationMethodRow: (value) =>
    set((draft) => {
      draft.interpolationMethodRow = value;
    }),
  setValueUomRow: (value) =>
    set((draft) => {
      draft.valueUomRow = value;
    }),
  setTimezone: (value) =>
    set((draft) => {
      draft.timezone = value;
    }),
  setDescriptionRow: (value) =>
    set((draft) => {
      draft.descriptionRow = value;
    }),
  setValueColumnIndices: (value) =>
    set((draft) => {
      draft.valueColumnIndices = value;
    }),
  setValueColumnNames: (value) =>
    set((draft) => {
      draft.valueColumnNames = value;
    }),
  setNameRow: (value) =>
    set((draft) => {
      draft.nameRow = value;
    }),
  setDescription: (value) =>
    set((draft) => {
      draft.description = value;
    }),
  setValueColumnOption: (value) =>
    set((draft) => {
      draft.valueColumnOption = value;
    }),
  setValueUom: (value) =>
    set((draft) => {
      draft.valueUom = value;
    }),
  setLenientDaylightSavings: (value) =>
    set((draft) => {
      draft.lenientDaylightSavings = value;
    }),
  setMaxDuration: ({ value, units }) =>
    set((draft) => {
      draft.maxDuration = { value, units };
    }),
  setEndColumnIndex: (value) =>
    set((draft) => {
      draft.endColumnIndex = value;
    }),
  setEndColumnName: (value) =>
    set((draft) => {
      draft.endColumnName = value;
    }),
  setConditionName: (value) =>
    set((draft) => {
      draft.conditionName = value;
    }),
  setDayFirstDefault: (value) =>
    set((draft) => {
      draft.dayFirstDefault = value;
    }),
  /** Changing item type also selects a compatible value column option. */
  setItemType: (itemType) =>
    set((draft) => {
      draft.itemType = itemType;
      if (itemType.value === ItemTypeEnum.Signal && draft.valueColumnOption.value === 'None') {
        draft.valueColumnOption = SIGNAL_VALUE_COLUMN_OPTIONS[0];
      }
      if (itemType.value === ItemTypeEnum.Condition) {
        draft.valueColumnOption = COLUMN_NAME;
      }
    }),
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),
  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),
  rehydrateForEdit: (payload) =>
    set((draft) => {
      Object.assign(draft, payload);
    }),
  reset: () => resetState(),
});

const STORE_NAME = 'ImportDatafile';
const [ImportDatafileStoreProvider, useImportDatafileStoreInstance, useImportDatafileStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  importDatafileActionsFactory,
);

export { ImportDatafileStoreProvider };
export const useImportDatafileUploadFilename = () => useImportDatafileStoreSelector((state) => state.uploadFilename);
export const useImportDatafileFilename = () => useImportDatafileStoreSelector((state) => state.filename);
export const useImportDatafileKeyColumnIndex = () => useImportDatafileStoreSelector((state) => state.keyColumnIndex);
export const useImportDatafileKeyColumnName = () => useImportDatafileStoreSelector((state) => state.keyColumnName);
export const useImportDatafileInterpolationMethod = () =>
  useImportDatafileStoreSelector((state) => state.interpolationMethod);
export const useImportDatafileValidationMode = () => useImportDatafileStoreSelector((state) => state.validationMode);
export const useImportDatafileFileOutputType = () => useImportDatafileStoreSelector((state) => state.fileOutputType);
export const useImportDatafileFieldDelimiter = () => useImportDatafileStoreSelector((state) => state.fieldDelimiter);
export const useImportDatafileMaxInterpolation = () =>
  useImportDatafileStoreSelector((state) => state.maxInterpolation);
export const useImportDatafileNamePrefix = () => useImportDatafileStoreSelector((state) => state.namePrefix);
export const useImportDatafileNameSuffix = () => useImportDatafileStoreSelector((state) => state.nameSuffix);
export const useImportDatafileFirstDataRow = () => useImportDatafileStoreSelector((state) => state.firstDataRow);
export const useImportDatafileMaxInterpolationRow = () =>
  useImportDatafileStoreSelector((state) => state.maxInterpolationRow);
export const useImportDatafileInterpolationMethodRow = () =>
  useImportDatafileStoreSelector((state) => state.interpolationMethodRow);
export const useImportDatafileValueUomRow = () => useImportDatafileStoreSelector((state) => state.valueUomRow);
export const useImportDatafileTimezone = () => useImportDatafileStoreSelector((state) => state.timezone);
export const useImportDatafileDescriptionRow = () => useImportDatafileStoreSelector((state) => state.descriptionRow);
export const useImportDatafileValueColumnIndices = () =>
  useImportDatafileStoreSelector((state) => state.valueColumnIndices);
export const useImportDatafileValueColumnNames = () =>
  useImportDatafileStoreSelector((state) => state.valueColumnNames);
export const useImportDatafileNameRow = () => useImportDatafileStoreSelector((state) => state.nameRow);
export const useImportDatafileDescription = () => useImportDatafileStoreSelector((state) => state.description);
export const useImportDatafileValueColumnOption = () =>
  useImportDatafileStoreSelector((state) => state.valueColumnOption);
export const useImportDatafileValueUom = () => useImportDatafileStoreSelector((state) => state.valueUom);
export const useImportDatafileLenientDaylightSavings = () =>
  useImportDatafileStoreSelector((state) => state.lenientDaylightSavings);
export const useImportDatafileItemType = () => useImportDatafileStoreSelector((state) => state.itemType);
export const useImportDatafileMaxDuration = () => useImportDatafileStoreSelector((state) => state.maxDuration);
export const useImportDatafileEndColumnIndex = () => useImportDatafileStoreSelector((state) => state.endColumnIndex);
export const useImportDatafileEndColumnName = () => useImportDatafileStoreSelector((state) => state.endColumnName);
export const useImportDatafileConditionName = () => useImportDatafileStoreSelector((state) => state.conditionName);
export const useImportDatafileDayFirstDefault = () => useImportDatafileStoreSelector((state) => state.dayFirstDefault);
export const useImportDatafileId = () => useImportDatafileStoreSelector((state) => state.id);
export const useImportDatafileName = () => useImportDatafileStoreSelector((state) => state.name);
export const useImportDatafileOriginalParameters = () =>
  useImportDatafileStoreSelector((state) => state.originalParameters);
export const useImportDatafileMaximumDuration = () => useImportDatafileStoreSelector((state) => state.maximumDuration);
export const useImportDatafileConfigVersion = () => useImportDatafileStoreSelector((state) => state.configVersion);
export const useImportDatafileAdvancedParametersCollapsed = () =>
  useImportDatafileStoreSelector((state) => state.advancedParametersCollapsed);
export const useImportDatafileActions = () => useImportDatafileStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateImportDatafileStore() {
  return createStoreInstance(initialState, importDatafileActionsFactory, { storeName: STORE_NAME });
}

export const usePersistImportDatafileStore = () => {
  const register = useRegisterPersistedStore();
  const store = useImportDatafileStoreInstance();
  useEffect(
    () =>
      register({
        storeName: 'sqImportDatafileStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateImportDatafileState,
      }),
    [register, store],
  );
};
